import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";

// Component Imports
import {
  AlertManager,
  AlgoManagement,
  Balances,
  Blotter,
  Dashboard,
  EntityManagement,
  Error,
  Landing,
  ManualAdjustments,
  NotFound,
  OktaCallback,
  OpenOrders,
  Overview,
  Positions,
  PositionSummary,
  RoleManagement,
  StaticPriceOverrides,
  StrategyManagement,
  TradingOverlay,
  Unauthorized,
} from "./src/pages";
import { SideNavbar } from "./src/layout";
import { ErrorBoundary } from "react-error-boundary";

// HOC Imports
import { AdminRoute, AuthorizedRoute, ProtectedRoute } from "./src/components";
import { AppContextProvider } from "./src/context/context";
import { ReduxGroupStateProvider } from "./src/features/groups/ReduxGroupStateProvider";
import { NavigationBridgeSync } from "./src/features/groups/NavigationBridgeSync";

// Utils Imports
import { isAuthenticated, grab_base_url } from "./src/utils/api_methods";

// WebSocket Manager — configure once at module load so all widgets share one connection pool
import { wsManager } from "@pulse-ui/websocket";
wsManager.configure({
  getToken: () => localStorage.getItem("pulsejwt"),
  buildUrl: (endpoint: string) => `${grab_base_url(window.location.origin, "wss")}${endpoint}`,
});
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => wsManager.disconnectAll());
  window.addEventListener("pagehide", () => wsManager.disconnectAll());
}

const App = () => {
  return (
    <ErrorBoundary
      fallback={<Error />}
      onError={(error, info) => {
        console.error("Error Boundary caught an error: ", error, info);
      }}
    >
      <AppContextProvider>
        <ReduxGroupStateProvider>
          <NavigationBridgeSync />
          <BrowserRouter>
            <SideNavbar />
            <Routes>
            <Route path="/" element={<Landing />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/alert-manager"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <AlertManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/algo-management"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <AlgoManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/entity-management"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <AdminRoute>
                    <EntityManagement />
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            <Route path="/error" element={<Error />} />
            <Route
              path="/manual-adjustments"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <AdminRoute>
                    <ManualAdjustments />
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            <Route path="/oauth2/callback" element={<OktaCallback />} />
            <Route
              path="/portfolio/balances"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <AuthorizedRoute>
                    <Balances />
                  </AuthorizedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/portfolio/blotter"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <AuthorizedRoute>
                    <Blotter />
                  </AuthorizedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/portfolio/open-orders"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <AuthorizedRoute>
                    <OpenOrders />
                  </AuthorizedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/portfolio/overview"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <AuthorizedRoute>
                    <Overview />
                  </AuthorizedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/portfolio/positions"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <AuthorizedRoute>
                    <Positions />
                  </AuthorizedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/portfolio/position-summary"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <AuthorizedRoute>
                    <PositionSummary />
                  </AuthorizedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/portfolio/trading-overlay"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <AuthorizedRoute>
                    <TradingOverlay />
                  </AuthorizedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/role-management"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <AdminRoute>
                    <RoleManagement />
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/static-price-overrides"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <StaticPriceOverrides />
                </ProtectedRoute>
              }
            />
            <Route
              path="/strategy-management"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <AdminRoute>
                    <StrategyManagement />
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        </ReduxGroupStateProvider>
      </AppContextProvider>
    </ErrorBoundary>
  );
};

export default App;
